import { useMemo } from "react";

import { renderToString } from "react-dom/server";

import { useTranslate } from "@similarweb/i18n-provider";
import {
    combineConfigs,
    fullMonthAndYear,
    shortMonthAndDay,
    shortMonthAndShortYear,
    shortMonthShortDayAndYear,
    xAxisLabelsConfig,
    yAxisLabelsConfig,
} from "@similarweb/pro-logic-components";
import { ChartTooltipTotal } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import dateTimeService from "app/services/date-time/dateTimeService";

import { LINE_CHART_HEIGHT, TOOLTIP_LEFT_COLUMN_WIDTH, TOOLTIP_RIGHT_COLUMN_WIDTH } from "./constants";
import { formatNumber } from "./utils/formatNumber";

export const useLineChartConfig = (isMonthly?: boolean): Highcharts.Options => {
    const { translate } = useTranslate();

    return useMemo(() => {
        const graphDateFormat = isMonthly ? shortMonthAndShortYear : shortMonthAndDay;
        const tooltipDateFormat = isMonthly ? fullMonthAndYear : shortMonthShortDayAndYear;

        return combineConfigs(
            {
                xAxisFormatter: ({ value }: { value: number }) =>
                    dateTimeService.formatWithMoment(value, graphDateFormat),
                yAxisFormatter: ({ value }: { value: number }) => formatNumber(value),
            },
            [
                xAxisLabelsConfig,
                yAxisLabelsConfig,
                {
                    chart: {
                        height: LINE_CHART_HEIGHT,
                    },
                    ...(isMonthly && {
                        xAxis: {
                            tickPositioner: function (this: Highcharts.Axis): Highcharts.AxisTickPositionsArray {
                                return this.series[0]?.data.map((point) => point.x) ?? [];
                            },
                        },
                    }),
                    yAxis: {
                        softMin: 0,
                        softMax: 1,
                    },
                    tooltip: {
                        enabled: true,
                        outside: true,
                        useHTML: true,
                        shared: true,
                        formatter: function (this) {
                            return renderToString(
                                <ChartTooltipTotal
                                    columnsSize={`${TOOLTIP_LEFT_COLUMN_WIDTH} ${TOOLTIP_RIGHT_COLUMN_WIDTH}`}
                                    title={dateTimeService.formatWithMoment(this.x, tooltipDateFormat)}
                                    headers={[
                                        translate("common.domain"),
                                        <Flex
                                            width={TOOLTIP_RIGHT_COLUMN_WIDTH}
                                            justifyContent="end"
                                        >
                                            {translate("common.volume")}
                                        </Flex>,
                                    ]}
                                    rows={(this.points ?? []).map((point) => ({
                                        displayName: point.series.name,
                                        firstValue: (
                                            <Flex
                                                width={TOOLTIP_RIGHT_COLUMN_WIDTH}
                                                justifyContent="end"
                                            >
                                                {formatNumber(point.y ?? 0)}
                                            </Flex>
                                        ),
                                        color: point.series.color?.toString() ?? "",
                                    }))}
                                />,
                            );
                        },
                    },
                },
            ],
        );
    }, [isMonthly, translate]);
};
